import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import config from "../../common/Config";
import ActiveProductList from './ActiveProductList';
import InactiveProductList from './InactiveProductList';
import AllProductList from './AllProductList';
import ChannelBrandList from './ChannelBrandList';
import BulkPriceUpdate from './BulkPriceUpdate';
import { Box, Paper, Tabs, Tab, Button, Typography, Grid2 } from '@mui/material';
import { Iconify } from "src/index";
import "../../common/commonStyles.css";
import { toast } from "sonner";
import { pushQuickBooksMaster, resolveQuickBooksEnabled } from "../../../utils/quickBooksTenant";
const Products = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pushingQb, setPushingQb] = useState(false);
  const [showQuickBooks, setShowQuickBooks] = useState(false);
  const accessToken = localStorage.getItem('accessToken');

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    resolveQuickBooksEnabled().then(setShowQuickBooks);
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedIds([]);
  };

  const handleAddToShopify = async () => {
    if (!selectedIds.length) return;
    const formData = new FormData();
    selectedIds.forEach((id) => formData.append("val[]", id));
    try {
      const response = await fetch(`${config.API_URL}/shopify/submit-products-to-shopify`, {
        method: "POST",
        headers: { authorization: `Bearer ${accessToken}` },
        body: formData,
      });
      if (response.ok) setSelectedIds([]);
    } catch (_) { }
  };

  const tabMap = {
    0: 'eur',
    1: 'gbp',
    2: 'usd',
    3: 'channel-brand',
    4: 'bulk-price'
  };

  const currentTabId = tabMap[activeTab];

  const handlePushQuickBooks = async () => {
    if (pushingQb) return;
    setPushingQb(true);
    try {
      const data = await pushQuickBooksMaster("products");
      toast.success(data.message || data.msg || "Products pushed to QuickBooks");
    } catch (e) {
      toast.error(e.message || "Push failed");
    } finally {
      setPushingQb(false);
    }
  };

  const handleSageProducts = async () => {
    try {
      const opts = { method: 'POST', headers: { authorization: `Bearer ${accessToken}` } };
      await Promise.all([
        fetch(`${config.API_URL}/sage/fetch-products`, opts),
        fetch(`${config.API_URL}/sage/fetch-product-price-views`, opts),
      ]);
    } catch (_) { }
  };

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">
                  Products
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 5 }} gap={1} display="flex" justifyContent="flex-end">
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  className="common-tabs"
                  sx={{
                  }}
                >
                  <Tab label="Active Products" />
                  <Tab label="Inactive Products" />
                  <Tab label="All Products" />
                  <Tab label="Channel Bands" />
                  <Tab label="Bulk Change Price" />
                </Tabs>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 5 }} display="flex" justifyContent="end" alignItems="center" gap={1} flexWrap="nowrap">
                {config.PREFIX === "tsc" && (activeTab === 0 || activeTab === 1 || activeTab === 2) && selectedIds.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="logos:shopify" width={20} height={20} />}
                    onClick={handleAddToShopify}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    Add to Shopify
                  </Button>
                )}
                <Button
                  component={Link}
                  to={`${config.BASE_URL}/products/add_product`}
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" width={20} height={20} />}
                  className="button-variant-contained"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Add Product
                </Button>
                {(config.PREFIX === "ebu" || accessToken === "ebu" || config.PREFIX === "rhp" || accessToken === "rhp") && (
                <Button
                  component={Link}
                  to={`${config.BASE_URL}/products/add_product_dynamic`}
                  variant="outlined"
                  startIcon={<Iconify icon="eva:plus-fill" width={20} height={20} />}
                  className="button-variant-outlined"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Add Product Dynamic
                </Button>
                )}
                {showQuickBooks && (config.PREFIX === "atr" || accessToken === "atr") && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={pushingQb}
                    onClick={handlePushQuickBooks}
                    sx={{
                      color: "#000",
                      borderColor: "#000",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      height: 34,
                      "&:hover": {
                        borderColor: "#000",
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    {pushingQb ? "Pushing..." : "Push to QuickBooks"}
                  </Button>
                )}
              </Grid2>
            </Grid2>
          </Box>
          <Box sx={{ pb: 2 }}>
            {activeTab === 0 && (
              <Box sx={{ width: "100%" }}>
                <ActiveProductList
                  refresh={currentTabId === 'eur'}
                  {...(config.PREFIX === "tsc" && { selectedIds, setSelectedIds })}
                />
              </Box>
            )}
            {activeTab === 1 && (
              <Box sx={{ width: "100%" }}>
                <InactiveProductList
                  refresh={currentTabId === 'gbp'}
                  {...(config.PREFIX === "tsc" && { selectedIds, setSelectedIds })}
                />
              </Box>
            )}
            {activeTab === 2 && (
              <Box sx={{ width: "100%" }}>
                <AllProductList
                  refresh={currentTabId === 'usd'}
                  {...(config.PREFIX === "tsc" && { selectedIds, setSelectedIds })}
                />
              </Box>
            )}
            {activeTab === 3 && (
              <Box sx={{ width: "100%" }}>
                <ChannelBrandList refresh={currentTabId === 'channel-brand'} />
              </Box>
            )}
            {activeTab === 4 && (
              <Box sx={{ width: "100%" }}>
                <BulkPriceUpdate />
              </Box>
            )}
          </Box>
        </Paper>
      </Box >
    </Box >
  );
};

export default Products;
